import {useObjectRecord} from '@/matic/features/master-data/hooks/useObjectRecord';
import type {ChipAssociation} from '../../types/relay-types';

type Props = {
  assoc: ChipAssociation;
  /** Prefix with "{objectTypeName}: ". Defaults to false. */
  includeTypeName?: boolean;
  /** Shimmer span classes — match the chip's color palette. */
  shimmerClassName?: string;
  className?: string;
};

export function AssociationLabel({
  assoc,
  includeTypeName = false,
  shimmerClassName = 'inline-block h-3 w-20 animate-pulse rounded bg-neutral-200',
  className,
}: Props) {
  const isTypeOnly = assoc.objectRecordId === null;
  const recordQuery = useObjectRecord(
    assoc.objectTypeId,
    assoc.objectRecordId ?? undefined
  );

  // Type-only ("Category") association: the type name IS the label — no
  // record to fetch. An unresolvable type renders a neutral placeholder,
  // never a raw UUID.
  if (isTypeOnly) {
    return (
      <span className={className}>
        {assoc.objectTypeName ?? 'Unavailable category'}
      </span>
    );
  }

  const r = recordQuery.data;
  const resolved = r?.display ?? r?.code;
  const fallback = assoc.objectRecordName ?? assoc.objectRecordCode;
  const recordLabel = resolved ?? fallback ?? 'Unknown';
  const isLoading = recordQuery.isLoading && !resolved && !fallback;

  if (isLoading) return <span className={shimmerClassName} />;

  return (
    <span className={className}>
      {includeTypeName && assoc.objectTypeName
        ? `${assoc.objectTypeName}: ${recordLabel}`
        : recordLabel}
    </span>
  );
}
